// `remainingShare` was this module's whole arithmetic until the thresholds
// replaced it (SPEC-007 D-76). It still draws the clock's own bar, and it is
// `duelClock`'s to export — a scoring module that re-exported it would be
// claiming a reading it no longer takes.
import { roundSeconds } from './duelClock';
import { LAST_STAGE_ROUND, roundGap, roundStage, roundTier } from './duelDifficulty';

/**
 * What a round is worth (SPEC-007 PR-7.7).
 *
 * A streak says how many you got right and says nothing about how you got them,
 * and two readers can reach the same nine by very different routes: one who knew
 * every answer in a second and one who ran the clock to its last tick every
 * time. A score is what tells those two apart, and it is what a leaderboard can
 * be built on — a board ranked on streak alone is a board most of whose entries
 * are ties.
 *
 * So a round pays a BASE for being right and MULTIPLIES it for being quick: two
 * hundred under two seconds, three hundred under one. Three prices, and a reader
 * learns all three from one screen — which is the whole point of them, because
 * the thing this module is trying to buy is a reader who ANSWERS rather than
 * deliberates.
 *
 * IT WAS A SLIDING BONUS AND THE SLIDE WAS INVISIBLE (SPEC-007 D-76). The bonus
 * used to be a share of the allowance — answer with four fifths of the clock
 * left, take four fifths of the bonus — which is a defensible curve that nobody
 * can see. Two rounds both said "correct", both rolled the score up, and one was
 * worth 267 against 233 for reasons no screen mentioned. A threshold is the
 * opposite: it is a line the reader can feel themselves crossing, and crossing
 * it is the reward.
 *
 * MEASURED IN SECONDS, NOT IN SHARES, and that reverses this module's oldest
 * decision on purpose. The share existed because paying on the CLOCK's remainder
 * would make late rounds cheaper as they got harder — three seconds left is a
 * fifth of round one and three fifths of round eleven. What a threshold pays on
 * is neither: it is the reader's own REACTION, which the allowance does not
 * touch. A 1.5-second answer is worth two hundred in round one and two hundred
 * in round twenty; the game gets harder because the clock gets shorter, and the
 * pay rate of a reader who keeps their speed never moves. That is what the share
 * was reaching for and could not express until `duelRun` started measuring the
 * interval itself (SPEC-007 D-73).
 *
 * AND WHAT A ROUND IS WORTH BEFORE ANY OF THAT IS A PROFILE (SPEC-007 D-78). A
 * flat base paid the same for "which of these two is faster" in round one as for
 * "which of these two covers a quarter mile first, and they are four hundredths
 * apart" in round fifteen — so the half of the game that gets harder was the
 * half that paid nothing for it. The profile adds a little for the QUESTION
 * (which subject, how close the two figures are) and a little for the
 * PROGRESSION (how far into the run it is), and it is deliberately a LITTLE:
 * this is a game about knowing cars, not an idle clicker, and a curve that
 * doubled every few rounds would make the first ten of a run worth nothing.
 *
 * BOTH ARE READ OFF `duelDifficulty`, which is the table the DEAL already uses.
 * That is the whole of why this stays honest: the questions a round may ask and
 * what it pays for them are one decision written once. A second table here would
 * be a second opinion about where the middle of a run is.
 *
 * Pure. It never reads a clock; the seconds are handed to it.
 */

/** What being right is worth, before any of it is about speed. */
export const BASE_ROUND_POINTS = 100;

/**
 * What being quick multiplies it by, and how quick you have to be.
 *
 * FASTEST FIRST, because that is the order `speedMultiplier` reads them in and a
 * list sorted the other way would pay every fast answer at the slower tier. Two
 * tiers rather than five: a reader has to be able to hold the whole rule in
 * their head while a clock is running, and "under two, under one" is a rule you
 * can hold at speed. `under` is the exclusive edge — 1,000ms is not under a
 * second, and a boundary that paid triple for exactly one second would be a rule
 * that reads differently from the way it is written.
 *
 * IN MILLISECONDS, because that is what `duelRun` measures. A tier quoted in
 * seconds would be a second unit in a module that already has one, and the
 * conversion would live at whichever call site remembered it.
 */
export const SPEED_MULTIPLIERS = [
    { under: 1000, times: 3 },
    { under: 2000, times: 2 },
];

/** The best a round can be multiplied by, for the two things that need a ceiling. */
export const TOP_SPEED_MULTIPLIER = SPEED_MULTIPLIERS[0].times;

/**
 * What speed can add on top, which is now a consequence rather than a constant.
 *
 * Kept because it is the number two other modules ask this one for — the gap
 * between what being right pays and what being right AND FAST pays — and losing
 * the name would leave them subtracting two constants at their own call sites.
 */
export const SPEED_ROUND_POINTS = BASE_ROUND_POINTS * (TOP_SPEED_MULTIPLIER - 1);

/**
 * What the QUESTION adds, by the tier of the subject it asks about.
 *
 * Indexed by `duelSpecs`' own difficulty, which is 1, 2 or 3 — so the first
 * entry is a placeholder for an index that cannot occur, and tier one adds
 * nothing at all. That is the point of it rather than an oversight: top speed
 * and power are what the base rate IS, and everything above them is priced
 * against them.
 */
export const TIER_POINTS = [0, 0, 20, 40];

/**
 * What a CLOSE pairing adds, and how close is close.
 *
 * The gap that separates the curve's opening from its middle, so "close" here
 * means exactly what it means to the deal: a pairing the first three rounds are
 * not allowed to ask. Two cars four per cent apart on torque is a different
 * question from two cars forty per cent apart, and the reader who gets it right
 * has done a different thing.
 */
export const CLOSE_CALL_GAP = 0.12;
export const CLOSE_CALL_POINTS = 20;

/**
 * What the ROUND adds, one entry per row of the difficulty curve.
 *
 * Priced against the curve's own rows rather than per round, which is what keeps
 * it LIMITED in the way the brief asked for: it steps three times over a whole
 * run and then stops, where a few points a round would have made round thirty
 * worth four of round one. The last row is where the game stops helping — the
 * pool opens up completely — so it is also where the reward stops climbing.
 *
 * The steps DECELERATE — fifteen, fifteen, ten — so the profile flattens as it
 * climbs rather than running away. Added to the dearest question in the game it
 * lands on exactly twice the base, which is the whole of the limit and is
 * asserted rather than remembered: the dearest round is worth two of the
 * cheapest before speed touches either, and speed can treble both.
 *
 * One entry per row is a contract with `duelDifficulty`: a fifth row added there
 * without a price here would silently pay it at the fourth row's rate, and
 * `duelScoring.test.js` fails on the length rather than letting that happen.
 */
export const PROGRESSION_POINTS = [0, 15, 30, 40];

/**
 * What this round is worth before speed touches it.
 *
 * `duel` is the pool entry the round was dealt — it carries both halves of the
 * difficulty the deal used to choose it — and a round without one still pays: a
 * missing entry is a bug upstream, and a round that paid nothing for it would
 * turn that bug into a reader's lost points.
 */
export function roundValue(duel, round) {
    const tier = TIER_POINTS[Math.trunc(duel?.difficulty) || 0] ?? 0;
    // Anything unreadable is treated as a wide pairing, which is the cheaper of
    // the two: a bonus is something to be earned rather than defaulted into.
    const gap = Number.isFinite(duel?.gap) ? duel.gap : 1;
    const closeCall = gap <= CLOSE_CALL_GAP ? CLOSE_CALL_POINTS : 0;
    const progression = PROGRESSION_POINTS[roundStage(round)]
        ?? PROGRESSION_POINTS[PROGRESSION_POINTS.length - 1];

    return BASE_ROUND_POINTS + tier + closeCall + progression;
}

/**
 * The most this round could possibly pay: the hardest question its own row is
 * allowed to deal, answered instantly.
 *
 * Asked of the CURVE rather than of the pool, which is what makes it a bound
 * rather than a measurement — the pool changes with the reader's garage, and a
 * ceiling that moved with it could not be used to check anything. It is what the
 * board's plausibility check is built from, and what the collection's ladder
 * keeps its first rung above.
 */
export function maxRoundPoints(round) {
    return roundValue(
        { difficulty: roundTier(round), gap: roundGap(round) },
        round,
    ) * TOP_SPEED_MULTIPLIER;
}

/**
 * The most any single round can pay, for the two things that need a ceiling.
 *
 * Taken from the LAST row of the curve, because that is where the profile stops
 * climbing: every round from that one on shares this ceiling and none can beat
 * it. It is NOT what an early round can pay, and the difference is load-bearing
 * — the collection's first rung is priced above `maxRoundPoints(1)` rather than
 * above this, or the first car would cost four rounds instead of two
 * (SPEC-010 D-28).
 */
export const MAX_ROUND_POINTS = maxRoundPoints(LAST_STAGE_ROUND);

/**
 * What a reaction of this many milliseconds multiplies the round by.
 *
 * One rather than nothing for anything unreadable: a round nobody can time is a
 * round that pays for being right, which is the floor this module has always
 * had. It never returns zero — a correct answer is never worth nothing.
 */
export function speedMultiplier(reaction) {
    if (!Number.isFinite(reaction) || reaction < 0) {
        return 1;
    }

    return SPEED_MULTIPLIERS.find(({ under }) => reaction < under)?.times ?? 1;
}

/**
 * The points for answering this round correctly with `secondsLeft` on the clock.
 *
 * A wrong answer and a round the clock ran out on are both worth nothing, and
 * that is the caller's business rather than this module's — it is only ever
 * asked about a round that was won.
 *
 * The floor is the base: a correct answer given on the last tick still pays for
 * being correct. A round that could pay nothing would make the last second of
 * every round worthless, and a reader who noticed would stop answering in it.
 */
export function roundPoints(secondsLeft, round, duel = null) {
    // The reaction, read back out of the clock rather than taken as a fourth
    // argument: `secondsLeft` is the allowance minus the time the reader spent,
    // so the time they spent is the allowance minus `secondsLeft`. It is exactly
    // the number `duelRun` computed to produce the seconds in the first place.
    const spent = roundSeconds(round) - (Number.isFinite(secondsLeft) ? secondsLeft : 0);

    return roundValue(duel, round) * speedMultiplier(Math.max(0, spent) * 1000);
}

/**
 * How much of a round's payment was BOUGHT WITH SPEED, from 0 to 1.
 *
 * The reading behind the reaction time the hero draws (SPEC-007 D-73). It is
 * here rather than in the component because it is the same division `roundPoints`
 * is the multiplication of, and a component that derived it would be a second
 * copy of the formula — one that a change to either constant would leave behind
 * without failing anything.
 *
 * Answered from the POINTS rather than from the clock, which is what makes it
 * agree with what the reader was actually paid: it is the payment, read back.
 * Since the payment became a multiplier it answers one of three values — nothing,
 * a half, all of it — which is the tier the round landed in and is exactly what
 * a three-step ramp should be drawn from.
 */
export function speedShare(points) {
    const bonus = (Math.max(0, points) || 0) - BASE_ROUND_POINTS;

    return Math.min(Math.max(bonus / SPEED_ROUND_POINTS, 0), 1);
}

/**
 * A reaction, in milliseconds, as the number of SECONDS a screen should draw.
 *
 * Two decimal places, because that is what a reaction time is: a tenth is too
 * coarse to tell two quick answers apart and a thousandth is a measurement of
 * the browser rather than of the reader. Rounded rather than truncated, and
 * arrived at through hundredths of a second rather than `toFixed`, which is
 * `accelerationTrace`'s note in this repo already — `(3.65).toFixed(1)` is
 * "3.6", because 3.65 is stored a hair below itself.
 *
 * A NUMBER rather than a string: which decimal separator a reader gets is
 * `formatSpecNumber`'s business and it is not the same in every language.
 */
export function reactionSeconds(reaction) {
    if (!Number.isFinite(reaction) || reaction < 0) {
        return 0;
    }

    return Math.round(reaction / 10) / 100;
}

/**
 * The best a run of `rounds` correct answers could possibly have scored.
 *
 * A SUM rather than a multiplication since the profile arrived (D-78): rounds
 * are not worth the same as each other any more, so a run of five is worth the
 * five cheapest ceilings and not five of the dearest. The board's plausibility
 * check is built on this, and multiplying instead would call an honest fast run
 * on the tenth round a forgery.
 *
 * Not used to score anything — it is the BOUND, and what lets a screen or a test
 * say "this run was worth 82% of a perfect one" without re-deriving the profile.
 */
export function perfectScore(rounds) {
    const played = Math.max(0, Math.trunc(rounds) || 0);
    let best = 0;

    for (let round = 1; round <= played; round += 1) {
        best += maxRoundPoints(round);
    }

    return best;
}

export { roundSeconds };
